// src/pages/CostTemplates.js
import React, { useEffect, useState, useRef } from "react";
import {
  db,
  collection,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  deleteDoc,
  serverTimestamp,
} from "../firebase";
import { useNavigate, useLocation } from "react-router-dom";
import LoadingSpinner from "../components/LoadingSpinner";
import ConfirmModal from "../components/ConfirmModal";
import currenciesData from "../data/currencies.json";

// Error Boundary
class ErrorBoundary extends React.Component {
  state = { error: null };
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return <div>Error: {this.state.error.message}</div>;
    }
    return this.props.children;
  }
}

const FeedbackModal = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => onClose(), 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: "400px", padding: "20px" }}>
        <h3>{type === "success" ? "Success" : "Error"}</h3>
        <p style={{ color: type === "success" ? "#15803d" : "#b91c1c" }}>
          {message}
        </p>
        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const RateFetchModal = ({ isFetching, retryCount, onRetry, onClose }) => {
  const [timeLeft, setTimeLeft] = useState(5);
  const modalRef = useRef(null);

  useEffect(() => {
    if (isFetching) {
      setTimeLeft(5);
      const timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [isFetching]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  return (
    <div className="modal-overlay">
      <div className="modal" ref={modalRef} style={{ maxWidth: "400px", padding: "20px" }}>
        <h3>Fetching Exchange Rates</h3>
        {isFetching ? (
          <>
            <p>Loading exchange rates... ({timeLeft}s)</p>
            <div className="loading-animation">
              <div className="spinner"></div>
            </div>
          </>
        ) : (
          <>
            <p>Failed to fetch exchange rates. Retry count: {retryCount}</p>
            <div className="modal-actions">
              <button className="btn-primary" onClick={onRetry}>
                Retry Now
              </button>
              <button className="btn-secondary" onClick={onClose}>
                Try Again Later
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default function CostTemplates() {
  const navigate = useNavigate();
  const location = useLocation();
  const { defaultCurrency: navDefaultCurrency = "USD", rates: initialRates = {} } = location.state || {};
  const [defaultCurrency, setDefaultCurrency] = useState(
    navDefaultCurrency || localStorage.getItem("defaultCurrency") || "USD"
  );
  const [rates, setRates] = useState(initialRates);
  const [templates, setTemplates] = useState([]);
  const [borderPosts, setBorderPosts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTab, setModalTab] = useState("default");
  const [editMode, setEditMode] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isViewing, setIsViewing] = useState(false);
  
  // Form state for create/edit
  const [formName, setFormName] = useState("");
  const [formType, setFormType] = useState("");
  const [formBorder, setFormBorder] = useState("");
  const [formEntries, setFormEntries] = useState([]);
  const [entryName, setEntryName] = useState("");
  const [entryCost, setEntryCost] = useState("");
  const [entryCurrency, setEntryCurrency] = useState("");
  
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);
  const [isFetchingRates, setIsFetchingRates] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [confirmModal, setConfirmModal] = useState(null);
  const modalRef = useRef(null);
  const activeButtonRef = useRef(null);

  // Load border posts for selection
  useEffect(() => {
    async function loadBorderPosts() {
      try {
        const snaps = await getDocs(collection(db, "border_posts"));
        const data = snaps.docs.map((d) => ({ id: d.id, ...d.data() }));
        setBorderPosts(data);
      } catch (error) {
        console.error("Error loading border posts:", error);
      }
    }
    loadBorderPosts();
  }, []);

  useEffect(() => {
    async function loadTemplates() {
      setLoading(true);
      setFeedback(null);
      try {
        const snaps = await getDocs(collection(db, "clearances"));
        const data = snaps.docs.map((d) => ({ id: d.id, ...d.data() }));
        console.log("Cost templates:", data);
        setTemplates(data);
        const ratesData = await fetchRatesWithRetry();
        setRates(ratesData);
      } catch (error) {
        console.error("Error loading cost templates:", error);
        setFeedback({ message: "Failed to load cost templates: " + error.message, type: "error" });
      } finally {
        setLoading(false);
      }
    }
    loadTemplates();

    const interval = setInterval(() => {
      fetchRatesWithRetry(true);
    }, 3600000);
    return () => clearInterval(interval);
  }, [defaultCurrency]);

  async function fetchRatesWithRetry(isBackground = false) {
    if (!isBackground) setIsFetchingRates(true);
    setRetryCount((prev) => prev + 1);
    try {
      const res = await fetch(`https://api.exchangerate-api.com/v4/latest/${defaultCurrency}`);
      if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
      const data = await res.json();
      setRates(data.rates || {});
      setIsFetchingRates(false);
      setRetryCount(0);
      localStorage.setItem(`rates_${defaultCurrency}`, JSON.stringify({ rates: data.rates, timestamp: Date.now() }));
      return data.rates || {};
    } catch (e) {
      console.error("Failed to fetch rates:", e);
      const cached = localStorage.getItem(`rates_${defaultCurrency}`);
      if (cached) {
        const { rates, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < 3600000) {
          setRates(rates);
          setIsFetchingRates(false);
          setRetryCount(0);
          return rates;
        }
      }
      setIsFetchingRates(false);
      return {};
    }
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openDropdownId && activeButtonRef.current && !activeButtonRef.current.contains(e.target)) {
        setOpenDropdownId(null);
      }
    };
    
    const handleScroll = () => {
      if (openDropdownId) {
        setOpenDropdownId(null);
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [openDropdownId]);

  // Get all border post options as flat array for dropdown
  const getAllBorderPostOptions = () => {
    const options = [];
    borderPosts.forEach(bp => {
      if (bp.entries && Array.isArray(bp.entries)) {
        bp.entries.forEach(entry => {
          const option = `${entry.border_name} (${entry.country})`;
          if (!options.includes(option)) {
            options.push(option);
          }
        });
      }
    });
    return options;
  };

  function openCreateModal() {
    setIsCreating(true);
    setIsViewing(false);
    setEditMode(false);
    setSelected(null);
    setFormName("");
    setFormType("");
    setFormBorder("");
    setFormEntries([]);
    setModalOpen(true);
    setOpenDropdownId(null);
  }

  function openEditModal(template) {
    setIsCreating(false);
    setIsViewing(false);
    setEditMode(true);
    setSelected(template);
    setFormName(template.name || "");
    setFormType(template.consignmentType || "");
    setFormBorder(template.border || "");
    setFormEntries((template.entries || []).map((e, i) => ({
      id: i,
      ...e,
      costDefault: (e.cost / (rates[e.currency] || 1)) * (rates[defaultCurrency] || 1),
    })));
    setModalOpen(true);
    setModalTab("default");
    setOpenDropdownId(null);
  }

  function openViewModal(template) {
    setIsCreating(false);
    setIsViewing(true);
    setEditMode(false);
    setSelected(template);
    setModalOpen(true);
    setModalTab("default");
    setOpenDropdownId(null);
  }

  function closeModal() {
    setSelected(null);
    setModalOpen(false);
    setEditMode(false);
    setIsCreating(false);
    setIsViewing(false);
    setFormName("");
    setFormType("");
    setFormBorder("");
    setFormEntries([]);
    setEntryName("");
    setEntryCost("");
    setEntryCurrency("");
  }

  function handleMenuClick(id, event) {
    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + window.scrollY + 4,
      left: rect.left + window.scrollX - 160,
    });
    activeButtonRef.current = button;
    setOpenDropdownId(openDropdownId === id ? null : id);
  }

  function confirmDeleteTemplate(id, templateName) {
    setConfirmModal({
      title: "Delete Cost Template",
      message: `Are you sure you want to delete "${templateName}"? This action cannot be undone.`,
      confirmText: "Delete",
      confirmVariant: "danger",
      onConfirm: () => handleDelete(id),
    });
    setOpenDropdownId(null);
  }

  async function handleDelete(id) {
    try {
      await deleteDoc(doc(db, "clearances", id));
      setFeedback({ message: "Cost template deleted successfully.", type: "success" });
      const snaps = await getDocs(collection(db, "clearances"));
      setTemplates(snaps.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error("Error deleting cost template:", error);
      setFeedback({ message: "Failed to delete cost template: " + error.message, type: "error" });
    }
  }

  function handleAddEntry(e) {
    e.preventDefault();
    if (!entryName || !entryCost || !entryCurrency) {
      setFeedback({ message: "Entry name, cost, and currency are required.", type: "error" });
      return;
    }
    const cost = parseFloat(entryCost);
    const costDefault = (cost / (rates[entryCurrency] || 1)) * (rates[defaultCurrency] || 1);
    const newEntry = {
      id: Date.now(), // Use timestamp for unique ID
      name: entryName,
      cost: cost,
      currency: entryCurrency,
      costDefault: costDefault,
    };
    setFormEntries([...formEntries, newEntry]);
    setEntryName("");
    setEntryCost("");
    setEntryCurrency("");
    setFeedback(null);
  }

  function removeEntry(id) {
    setFormEntries(formEntries.filter((e) => e.id !== id));
  }

  async function saveTemplate() {
    if (!formName || !formBorder) {
      setFeedback({ message: "Template name and border/location are required.", type: "error" });
      return;
    }

    const totalCostDefault = formEntries.reduce((sum, e) => sum + (e.costDefault || 0), 0);

    try {
      if (isCreating) {
        await addDoc(collection(db, "clearances"), {
          name: formName,
          consignmentType: formType,
          border: formBorder,
          entries: formEntries.map(({ id, ...rest }) => rest),
          totalCostDefault,
          createdAt: serverTimestamp(),
        });
        setFeedback({ message: "Cost template created successfully.", type: "success" });
      } else if (selected) {
        await updateDoc(doc(db, "clearances", selected.id), {
          name: formName,
          consignmentType: formType,
          border: formBorder,
          entries: formEntries.map(({ id, ...rest }) => rest),
          totalCostDefault,
        });
        setFeedback({ message: "Cost template updated successfully.", type: "success" });
      }
      closeModal();
      const snaps = await getDocs(collection(db, "clearances"));
      setTemplates(snaps.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error("Error saving cost template:", error);
      setFeedback({ message: "Failed to save cost template: " + error.message, type: "error" });
    }
  }

  function returnToFinance() {
    navigate("/finance");
  }

  // Render border post dropdown
  const renderBorderDropdown = () => {
    const borderOptions = getAllBorderPostOptions();
    return (
      <div className="border-dropdown-wrapper">
        <select
          value={formBorder}
          onChange={(e) => setFormBorder(e.target.value)}
          className="border-select"
          required
        >
          <option value="">Select Border / Entry Point</option>
          {borderOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
    );
  };

  return (
    <ErrorBoundary>
      <div className="cost-templates-page">
        {isFetchingRates || retryCount > 0 ? (
          <RateFetchModal
            isFetching={isFetchingRates}
            retryCount={retryCount}
            onRetry={() => fetchRatesWithRetry()}
            onClose={() => setRetryCount(0)}
          />
        ) : feedback ? (
          <FeedbackModal
            message={feedback.message}
            type={feedback.type}
            onClose={() => setFeedback(null)}
          />
        ) : null}
        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            <div className="cost-templates-header">
              <h2>Cost Templates</h2>
              <div className="header-actions">
                <button className="btn-primary" onClick={openCreateModal}>
                  + Create New Template
                </button>
                <button className="btn-secondary" onClick={returnToFinance}>
                  Return to Finance
                </button>
              </div>
            </div>
            <div className="section">
              <div className="table-wrap">
                <table className="simple-table">
                  <thead>
                    <tr>
                      <th>Template Name</th>
                      <th>Type</th>
                      <th>Border/Location</th>
                      <th>Total ({defaultCurrency})</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {templates.length === 0 ? (
                      <tr>
                        <td colSpan="5" style={{ textAlign: "center" }}>No cost templates found. Click "Create New Template" to add one.</td>
                      </tr>
                    ) : (
                      templates.map((t) => (
                        <tr key={t.id}>
                          <td>{t.name || "-"}</td>
                          <td>{t.consignmentType || "-"}</td>
                          <td>{t.border || "-"}</td>
                          <td>{(t.totalCostDefault || 0).toFixed(2)}</td>
                          <td className="actions-cell">
                            <button
                              className="menu-button"
                              onClick={(e) => handleMenuClick(t.id, e)}
                            >
                              ⋮
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Create/Edit/View Modal */}
            {modalOpen && (
              <div className="modal-overlay">
                <div className="modal cost-template-modal" ref={modalRef}>
                  {isViewing ? (
                    // View Mode - Read Only
                    <>
                      <h3>📋 {selected?.name || "-"}</h3>
                      <div className="tab-container">
                        <button
                          className={`btn-tab ${modalTab === "default" ? "active" : ""}`}
                          onClick={() => setModalTab("default")}
                        >
                          Default Currency ({defaultCurrency})
                        </button>
                        <button
                          className={`btn-tab ${modalTab === "recorded" ? "active" : ""}`}
                          onClick={() => setModalTab("recorded")}
                        >
                          Recorded Currency
                        </button>
                      </div>
                      <div className="tab-content">
                        <div className="view-detail"><strong>Type:</strong> {selected?.consignmentType || "-"}</div>
                        <div className="view-detail"><strong>Border/Location:</strong> {selected?.border || "-"}</div>
                        <div className="entries-list">
                          <h4>Cost Items</h4>
                          <div className="entries-table">
                            <div className="entries-header">
                              <span>Item Name</span>
                              <span>Cost {modalTab === "default" ? `(${defaultCurrency})` : ""}</span>
                            </div>
                            {(selected?.entries || []).map((en, i) => (
                              <div key={i} className="entry-view-row">
                                <span className="entry-view-name">{en.name || "-"}</span>
                                <span className="entry-view-cost">
                                  {modalTab === "default"
                                    ? (en.costDefault || 0).toFixed(2)
                                    : `${(en.cost || 0).toFixed(2)} (${en.currency || "-"})`}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="modal-actions">
                        <button className="btn-primary" onClick={() => openEditModal(selected)}>
                          ✏️ Edit Template
                        </button>
                        <button className="btn-secondary" onClick={closeModal}>
                          Close
                        </button>
                      </div>
                    </>
                  ) : (
                    // Create/Edit Mode
                    <>
                      <h3>{isCreating ? "➕ Create New Cost Template" : `✏️ Edit: ${selected?.name}`}</h3>
                      <div className="form-block">
                        <input
                          value={formName}
                          onChange={(e) => setFormName(e.target.value)}
                          placeholder="Template Name (e.g., Beitbridge to Lusaka)"
                          className="template-name-input"
                        />
                        <input
                          value={formType}
                          onChange={(e) => setFormType(e.target.value)}
                          placeholder="Consignment Type (optional)"
                          className="template-type-input"
                        />
                        {renderBorderDropdown()}
                        
                        <h4>Cost Items</h4>
                        <div className="entry-input-row">
                          <input
                            value={entryName}
                            onChange={(e) => setEntryName(e.target.value)}
                            placeholder="Item name (e.g., Toll, Customs)"
                            className="entry-name-input"
                          />
                          <input
                            type="number"
                            value={entryCost}
                            onChange={(e) => setEntryCost(e.target.value)}
                            placeholder="Cost"
                            className="entry-cost-input"
                          />
                          <select
                            value={entryCurrency}
                            onChange={(e) => setEntryCurrency(e.target.value)}
                            className="entry-currency-select"
                          >
                            <option value="">Select Currency</option>
                            {currenciesData.map((c) => (
                              <option key={c.code} value={c.code}>{c.code}</option>
                            ))}
                          </select>
                          <button type="button" className="btn-secondary add-item-btn" onClick={handleAddEntry}>
                            + Add Item
                          </button>
                        </div>
                        
                        {formEntries.length > 0 && (
                          <div className="entries-table-container">
                            <div className="entries-table-header">
                              <span>Item Name</span>
                              <span>Cost (Recorded)</span>
                              <span>Cost ({defaultCurrency})</span>
                              <span></span>
                            </div>
                            {formEntries.map((entry) => (
                              <div key={entry.id} className="entry-table-row">
                                <span className="entry-name-col">{entry.name}</span>
                                <span className="entry-cost-col">{entry.cost} {entry.currency}</span>
                                <span className="entry-default-col">{entry.costDefault.toFixed(2)} {defaultCurrency}</span>
                                <button 
                                  type="button" 
                                  className="delete-entry-btn" 
                                  onClick={() => removeEntry(entry.id)}
                                  title="Delete item"
                                >
                                  🗑️
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="modal-actions">
                        <button className="btn-primary" onClick={saveTemplate}>
                          {isCreating ? "Create Template" : "Save Changes"}
                        </button>
                        <button className="btn-secondary" onClick={closeModal}>
                          Cancel
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {openDropdownId && (
        <div 
          className="cost-templates-dropdown-menu"
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            zIndex: 10000,
          }}
        >
          <div 
            className="dropdown-option" 
            onClick={() => {
              const template = templates.find(t => t.id === openDropdownId);
              if (template) openViewModal(template);
            }}
          >
            👁️ View Template
          </div>
          <div 
            className="dropdown-option" 
            onClick={() => {
              const template = templates.find(t => t.id === openDropdownId);
              if (template) openEditModal(template);
            }}
          >
            ✏️ Edit Template
          </div>
          <div 
            className="dropdown-option" 
            onClick={() => {
              const template = templates.find(t => t.id === openDropdownId);
              if (template) confirmDeleteTemplate(openDropdownId, template.name);
            }}
          >
            🗑️ Delete
          </div>
        </div>
      )}
      {confirmModal && (
        <ConfirmModal
          title={confirmModal.title}
          message={confirmModal.message}
          confirmText={confirmModal.confirmText}
          confirmVariant={confirmModal.confirmVariant}
          onConfirm={confirmModal.onConfirm}
          onClose={() => setConfirmModal(null)}
        />
      )}
    </ErrorBoundary>
  );
}